import { useReactiveVar } from '@apollo/client';
import { uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';

import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys';
import { mostRecentlyViewedHouseholdsVar } from '@/graphql/reactiveVars';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import {
  useSyncHouseholdsLazyQuery,
  useSyncHouseholdsQuery,
} from '../graphql/MostRecentlyViewedHouseholds.generated';

// Refresh inactive households that have no layout status subscription.
const VALIDATION_STATUS_REFRESH_INTERVAL_MS = 180_000;

interface MostRecentlyViewedHousehold {
  id: string;
  displayName: string;
  private?: boolean;
  hasPendingValidationQuestions?: boolean;
}

function persistMostRecentlyViewedHouseholds(
  mostRecentlyViewedHouseholds: MostRecentlyViewedHousehold[]
) {
  const key = `${LOCAL_STORAGE_KEYS.LAST_VIEWED_HOUSEHOLDS}_${location.hostname}`;
  localStorage.setItem(key, JSON.stringify(mostRecentlyViewedHouseholds));
}

function getInitialMostRecentlyViewedHouseholds() {
  if (typeof localStorage === 'undefined') return [];

  let households: MostRecentlyViewedHousehold[] = [];
  const localStorageKey = `${LOCAL_STORAGE_KEYS.LAST_VIEWED_HOUSEHOLDS}_${location.hostname}`;

  try {
    households = JSON.parse(
      localStorage.getItem(localStorageKey) || '[]'
    ) as MostRecentlyViewedHousehold[];
  } catch (err) {
    diagnostics.error(
      'Error parsing most recently viewed households',
      err as Error,
      {
        localStorageKey,
      }
    );
  }

  return households;
}

export function useMostRecentlyViewedHouseholds(
  activeHousehold?: MostRecentlyViewedHousehold
) {
  const mostRecentlyViewedHouseholds = useReactiveVar(
    mostRecentlyViewedHouseholdsVar
  );
  const enhanced = useFeatureFlag('enhanced_household_onboarding_flow');
  const { data: validationData } = useSyncHouseholdsQuery({
    variables: {
      where: { idIn: mostRecentlyViewedHouseholds.map(({ id }) => id) },
      includeValidationStatus: enhanced,
    },
    skip: !enhanced || mostRecentlyViewedHouseholds.length === 0,
    fetchPolicy: 'cache-and-network',
    pollInterval: VALIDATION_STATUS_REFRESH_INTERVAL_MS,
  });
  const [syncHouseholds, { data: legacyData }] = useSyncHouseholdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const data = enhanced ? validationData : legacyData;

  // When we view a householdId route, we call this function which optimistically
  // adds the household to the list of most recently viewed households
  // and queries the backend to sync the household data
  const addMostRecentlyViewedHousehold = useCallback(
    async (householdId: string, displayName: string, isPrivate: boolean) => {
      const newHousehold = {
        id: householdId,
        displayName,
        private: isPrivate,
      };

      const currentMostRecentlyViewedHouseholds =
        mostRecentlyViewedHouseholdsVar();

      const newMostRecentlyViewedHouseholds = uniqBy(
        [newHousehold, ...currentMostRecentlyViewedHouseholds],
        'id'
      ).slice(0, 5);

      if (!enhanced && newMostRecentlyViewedHouseholds.length > 0) {
        await syncHouseholds({
          variables: {
            where: {
              idIn: newMostRecentlyViewedHouseholds.map((h) => h.id),
            },
          },
          onError: (error) => {
            diagnostics.error('Error syncing households', error);
          },
        });
      }

      mostRecentlyViewedHouseholdsVar(newMostRecentlyViewedHouseholds);
      persistMostRecentlyViewedHouseholds(newMostRecentlyViewedHouseholds);
    },
    [enhanced, syncHouseholds]
  );

  const removeMostRecentlyViewedHousehold = useCallback(
    (householdId: string) => {
      const currentMostRecentlyViewedHouseholds =
        mostRecentlyViewedHouseholdsVar();
      const updatedHouseholds = currentMostRecentlyViewedHouseholds.filter(
        (household) => household.id !== householdId
      );

      mostRecentlyViewedHouseholdsVar(updatedHouseholds);
      persistMostRecentlyViewedHouseholds(updatedHouseholds);
    },
    []
  );

  useEffect(() => {
    // We might have to initialize the most recently viewed households
    if (mostRecentlyViewedHouseholds.length > 0) return;

    mostRecentlyViewedHouseholdsVar(getInitialMostRecentlyViewedHouseholds());
  }, [mostRecentlyViewedHouseholds.length]);

  // Returns the most recently viewed households with the synced display names
  // and removes any households that have been deleted
  const syncedMostRecentlyViewedHouseholds = useMemo<
    MostRecentlyViewedHousehold[]
  >(() => {
    const syncedHouseholds = getNodes(data?.households);
    if (!syncedHouseholds.length) return mostRecentlyViewedHouseholds;

    const syncedHouseholdIds = new Set(
      syncedHouseholds.map((household) => household.id)
    );

    // Remove any deleted households from the list
    // and update the display name if it has changed
    const updatedHouseholds = mostRecentlyViewedHouseholds
      .filter((household) => {
        const exists = syncedHouseholdIds.has(household.id);
        if (!exists) {
          removeMostRecentlyViewedHousehold(household.id);
        }
        return exists;
      })
      .map((household) => {
        const syncedHousehold = syncedHouseholds.find(
          (h) => h.id === household.id
        );
        return {
          ...household,
          displayName: syncedHousehold?.displayName ?? EMPTY_CONTENT_HYPHEN,
          private: syncedHousehold?.private ?? false,
          hasPendingValidationQuestions:
            syncedHousehold?.hasPendingValidationQuestions ?? false,
        };
      });

    return updatedHouseholds;
  }, [
    data?.households,
    mostRecentlyViewedHouseholds,
    removeMostRecentlyViewedHousehold,
  ]);

  const otherHouseholds = syncedMostRecentlyViewedHouseholds
    .filter(({ id }) => id !== activeHousehold?.id)
    .slice(0, 4);
  let switcherHouseholds = otherHouseholds;
  if (enhanced && activeHousehold) {
    const syncedActiveHousehold = syncedMostRecentlyViewedHouseholds.find(
      ({ id }) => id === activeHousehold.id
    );
    switcherHouseholds = [
      {
        ...activeHousehold,
        hasPendingValidationQuestions:
          syncedActiveHousehold?.hasPendingValidationQuestions,
      },
      ...otherHouseholds,
    ];
  }

  return {
    mostRecentlyViewedHouseholds: syncedMostRecentlyViewedHouseholds,
    switcherHouseholds,
    addMostRecentlyViewedHousehold,
    removeMostRecentlyViewedHousehold,
  };
}
